import React, { useContext, useMemo }  from 'react';
import { Redirect, Route, useLocation } from 'react-router-dom';

import { useCart } from '../cart/index.jsx';
import { UserContext, SettingsContext } from '../contexts/index.jsx';
import { RequireFeature } from '../components/RequireFeature';
import { NoStoreYet } from '../components/NoStoreYet';
import { setStorageKey } from '../core/utils/storage';
import { DefaultLayout } from './index.jsx';

const redirect = (to, returnTo) =>  {
    if (returnTo && returnTo !== '/logout') {
        setStorageKey('afterLoginRedirect', returnTo);
    }

    return <Redirect to={to} />
}

const EmptyContent = () => <></>

const featureGated = (Content, feature) => {
    const Gated = (contentProps) => (
        <RequireFeature feature={feature}>
            <Content {...contentProps} />
        </RequireFeature>
    )
    Gated.displayName = `RequireFeature(${feature})`
    return Gated
}

const CustomRoute = ({
    action,
    layout, withSimpleFooter = true, content, children,
    className, title, merchantHomeLink,
    requireAdmin, requireClient, requireMember, requireUser, requireGuest,
    // RC master admins only (platform pages: WhatsApp VMs / Coexistence,
    // Retail Circle Admin). Implies sign-in; anyone else gets /error/403.
    requireMasterAdmin,
    allowUnactivated = false,
    allowLimited = false,
    type,
    withSearch, withSearchFilters, withCategories, narrow,
    layoutProps, wideContent,
    topNavItems,
    allowShopify,
    allowRetailCircle,
    noTopBar,
    // Plan gating: an App\Constants\Features key. When the store's plan
    // doesn't include it, the page renders the locked screen (or the
    // "not available" variant for Future Release features) INSTEAD of the
    // content — so a direct URL is blocked, not just a hidden nav item.
    // Fail-open while entitlements load or enforcement is off; see
    // RequireFeature/EntitlementsContext.
    requireFeature,
    ...props
}) => {
    const userContext = useContext(UserContext)
    const cart = useCart();
    const { isShopify, isRetailCircle } = useContext(SettingsContext)
    const location = useLocation()

    // Keep the page chrome (layout, nav) and swap only the body, so a locked
    // page still looks like part of the app rather than a dead end.
    //
    // MEMOISED ON PURPOSE. This used to be built inline on every render, which
    // made it a brand-new component type each time. React treats a new type as
    // a different component, so every re-render of this route (a UserContext
    // update on tab focus, a notifications poll, a cart change) UNMOUNTED and
    // remounted the whole page: list back to "Loading…", open detail closed,
    // filters reset, scroll to top. Reported 2026-09-30 on /uptime as "the
    // page keeps reloading when I try to view something"; it hit every page
    // with requireFeature. Must stay above the early returns (rules of hooks).
    const ContentComponent = useMemo(
        () => (requireFeature
            ? featureGated(content || EmptyContent, requireFeature)
            : content || EmptyContent),
        [content, requireFeature]
    )

    const user = userContext.user
    // RC users: activated = OTP verified + admin approved. DAO: email verified.
    const userActivated = isRetailCircle
        ? user?.status === 'active' && user?.approval_status === 'approved'
        : user?.status === 'active' && user?.email_verified_at
    const userLimited = user?.status === 'limited_to_referred_product' && user?.email_verified_at

    if (isShopify && !allowShopify) {
      return null;
    }


    if (requireUser || requireMember || requireClient || requireAdmin || requireMasterAdmin) {
        if (!user) {
            return redirect('/sign-in', location.pathname)
        }

        if (!(userLimited && allowLimited) && !userActivated && !allowUnactivated) {
            return redirect('/profile')
        }
    }

    if (requireGuest && user) {
        return redirect('/')
    }

    if (requireAdmin && !user?.is_admin) {
        return redirect('/error/403')
    }

    if (requireMasterAdmin && !user?.is_master_admin) {
        return redirect('/error/403')
    }

    if (requireMember && !user) {
        return redirect('/error/403')
    }

    if (requireClient) {
        return redirect('/error/403')
    }

    if (action === 'logout') {
        userContext.logout()
        return <></>
    }

    // L12: an RC admin with no store at all (half-provisioned invitee) gets a
    // "store isn't set up yet" body instead of the page. The server treats a
    // non-master with no store as unscoped, so any store page could otherwise
    // end up showing another store's data. A store_user seat (accessibleStores)
    // counts as a store: the switcher lets them pick it. RC masters are
    // unscoped on purpose; legacy DAO-storefront users are untouched.
    const noStoreYet = isRetailCircle && requireAdmin && user
        && !user.is_master_admin && !user.store_id_m && !userContext.activeStoreId
        && !userContext.accessibleStores?.length

    const LayoutComponent = layout || DefaultLayout

    layoutProps = {
        action, className, title, merchantHomeLink, narrow, type,
        withSearch, withSearchFilters, withCategories, withSimpleFooter,
        topNavItems, wideContent, noTopBar,
        ...layoutProps
    }

    return (
        <Route
            {...props}
            render={() => (
                <LayoutComponent
                    content={noStoreYet ? NoStoreYet : ContentComponent}
                    context={{
                        user: userContext.user,
                        authentication: userContext,
                        cart,
                    }}
                    {...layoutProps}
                />
            )}
        />
    )
}

export default CustomRoute
